import React from "react";
import { Company } from "@/lib/hooks/api/companies";
import { Building2, Calendar, Settings } from "lucide-react";
import { formatDate } from "@/lib/utils/crm/time-entry-helpers";

interface CompanyInfoDisplayProps {
  company: Company;
  sections?: "company"[];
  variant?: "default" | "compact";
  showTitles?: boolean;
  className?: string;
}

export const CompanyInfoDisplay: React.FC<CompanyInfoDisplayProps> = ({
  company,
  sections = ["company"],
  variant = "default",
  showTitles = true,
  className = "",
}) => {
  const isCompact = variant === "compact";

  const renderCompanySection = () => (
    <div key="company" className="space-y-3">
      {showTitles && (
        <h5 className="text-sm font-medium text-card-foreground flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Company Information
        </h5>
      )}

      <div className="grid grid-cols-1 gap-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700 dark:text-gray-300">Name:</span>
          <span className="text-card-foreground">{company.name}</span>
        </div>

        {company.normalized_name && (
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-700 dark:text-gray-300">Normalized:</span>
            <span className="text-gray-600 dark:text-gray-400">{company.normalized_name}</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700 dark:text-gray-300">ID:</span>
          <span className="text-gray-600 dark:text-gray-400 font-mono text-xs">{company.id}</span>
        </div>

        <div className="flex items-center gap-2">
          <Calendar className="h-3 w-3 text-gray-400" />
          <span className="font-medium text-gray-700 dark:text-gray-300">Created:</span>
          <span className="text-gray-600 dark:text-gray-400">{formatDate(company.created_at)}</span>
        </div>

        <div className="flex items-center gap-2">
          <Calendar className="h-3 w-3 text-gray-400" />
          <span className="font-medium text-gray-700 dark:text-gray-300">Updated:</span>
          <span className="text-gray-600 dark:text-gray-400">{formatDate(company.updated_at)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`grid grid-cols-1 ${isCompact ? "gap-4" : "gap-6"} ${className}`}>
      {sections.map((section) => {
        switch (section) {
          case "company":
            return renderCompanySection();
          default:
            return null;
        }
      })}
    </div>
  );
};
